import React, { useCallback, useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    CircularProgress,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useAuth } from 'react-oidc-context';
import CreateApi from './Settings/appSettings';
import { useMCContext } from './contexts/useMCStates';
import { CollectionStandingsDisplay } from './CollectionStandingsDisplay';
import type { CollectionStandings } from '@moment-golf/types';

interface SeriesOption {
    _id: string;
    groupName: string;
    collectionStandings?: CollectionStandings;
    createdAt?: string;
    /** true when this series came from the authenticated user's played series */
    isUserSeries?: boolean;
}

function sortSeriesNewestFirst(list: SeriesOption[]): SeriesOption[] {
    return [...list].sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
    });
}

export const MCStandingsSection: React.FC = () => {
    const api = CreateApi();
    const auth = useAuth();
    const { factor, appTheme, windowSize } = useMCContext();
    const isAuthenticated = auth.isAuthenticated;

    const [publicSeries, setPublicSeries] = useState<SeriesOption[]>([]);
    const [userSeries, setUserSeries] = useState<SeriesOption[]>([]);
    const [loadingPublic, setLoadingPublic] = useState(true);
    const [loadingUser, setLoadingUser] = useState(false);
    const [selectedSeries, setSelectedSeries] = useState<SeriesOption | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Fetch public series (no token needed) on mount
    const fetchPublicSeries = useCallback(async () => {
        setLoadingPublic(true);
        setError(null);
        try {
            const res = await api.get('/mc/GetPublicSeries');
            setPublicSeries(
                sortSeriesNewestFirst(
                    (res.data as SeriesOption[]).map((s) => ({ ...s, isUserSeries: false })),
                ),
            );
        } catch {
            setError('Unable to load series. Please try again later.');
        } finally {
            setLoadingPublic(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch user-played series when authenticated
    const fetchUserSeries = useCallback(async () => {
        if (!isAuthenticated) return;
        setLoadingUser(true);
        try {
            const res = await api.get('/tb/GetUserPlayedSeries');
            setUserSeries(
                sortSeriesNewestFirst(
                    (res.data as SeriesOption[]).map((s) => ({ ...s, isUserSeries: true })),
                ),
            );
        } catch {
            // Non-fatal — user series are additive; public series still show
        } finally {
            setLoadingUser(false);
        }
    }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchPublicSeries(); }, [fetchPublicSeries]);
    useEffect(() => { fetchUserSeries(); }, [fetchUserSeries]);

    // Dedupe then sort newest → oldest by createdAt
    const mergedOptions: SeriesOption[] = sortSeriesNewestFirst([
        ...userSeries,
        ...publicSeries.filter((p) => !userSeries.some((u) => u._id === p._id)),
    ]);

    const isLoading = loadingPublic || loadingUser;

    const isMobileLayout =
        windowSize.isMobile ||
        windowSize.verticalPC !== 'none' ||
        ['small'].includes(windowSize.horizontalPC);

    return (
        <Stack
            direction="column"
            sx={{
                height: `calc(100dvh - ${120 * factor}px - env(safe-area-inset-bottom))`,
                overflow: 'hidden',
                px: 1,
                py: 1,
                flex: 1,
                minHeight: 0,
            }}
        >
            {/* Centered content area */}
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        maxWidth: 600,
                        mx: 'auto',
                        backgroundColor: 'white',
                        border: `1px solid ${appTheme.palette.divider}`,
                        borderRadius: 2,
                        p: 2,
                        overflow: 'hidden',
                    }}
                >
                    {/* Series selector */}
                    <Stack
                        direction={isMobileLayout ? 'column' : 'row'}
                        alignItems={isMobileLayout ? 'stretch' : 'center'}
                        spacing={1}
                        sx={{ flexShrink: 0, mb: 2 }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: appTheme.palette.primary.main,
                                fontSize: 15 * factor,
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Series Standings
                        </Typography>

                        <Autocomplete
                            options={mergedOptions}
                            value={selectedSeries}
                            onChange={(_, val) => setSelectedSeries(val)}
                            getOptionLabel={(opt) => opt.groupName}
                            isOptionEqualToValue={(a, b) => a._id === b._id}
                            loading={isLoading}
                            noOptionsText={
                                isLoading
                                    ? 'Loading…'
                                    : error
                                    ? error
                                    : 'No series available'
                            }
                            groupBy={(opt) => (opt.isUserSeries ? 'My Series' : 'Public Series')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select a series…"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isLoading && <CircularProgress size={16} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            fontSize: 13 * factor,
                                            backgroundColor: 'white',
                                        },
                                    }}
                                />
                            )}
                            sx={{ flex: 1, minWidth: 0 }}
                            ListboxProps={{ style: { maxHeight: 300 } }}
                        />
                    </Stack>

                    {/* Standings display */}
                    {!selectedSeries ? (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: 13 * factor }}
                            >
                                {isLoading
                                    ? 'Loading series…'
                                    : error
                                    ? error
                                    : mergedOptions.length === 0
                                    ? 'No series are currently available.'
                                    : 'Select a series above to view standings.'}
                            </Typography>
                        </Box>
                    ) : (
                        <CollectionStandingsDisplay
                            standings={selectedSeries.collectionStandings}
                            factor={factor}
                        />
                    )}
                </Paper>
            </Box>
        </Stack>
    );
};
